import { AUTH_ERRORS } from "../shared/types/websocket-auth";

import { store } from "store/index";
import { selectPageCapabilities, selectPageIdByWorksheetId } from "store/selectors";
import { logInfoWithDebugFlag, rollbarAndLogWarning } from "utils/logger";

export type DocReadAction = "subscribe" | "fetch";

/**
 * Determines a human-readable reason for a client-side doc read denial.
 *
 * - "client_server_mismatch": client still has permission at error time but
 *   server denied — unexpected, worth alerting
 * - "permission_revoked_after_<action>": client had permission when the request
 *   was initiated, but an update removed it before the error callback fired
 * - "no_page_permissions": client does not consider the page viewable
 */
export const getDocErrorReason = (
  docId: string,
  hadPermissionAtRequest: boolean,
  action: DocReadAction,
) => {
  const state = store.getState();
  const pageId = selectPageIdByWorksheetId(state, docId);
  const { canViewPage } = selectPageCapabilities(state, pageId);

  if (canViewPage) return "client_server_mismatch";
  if (hadPermissionAtRequest) return `permission_revoked_after_${action}`;
  return "no_page_permissions";
};

/** ShareDB attaches the protocol message as `err.data` in wrapErrorData. */
export const getDocIdFromShareDBError = (err: unknown): string | undefined => {
  if (typeof err !== "object" || err === null || !("data" in err)) return undefined;
  const data = err.data;
  if (typeof data !== "object" || data === null || !("d" in data)) return undefined;
  return typeof data.d === "string" ? data.d : undefined;
};

/**
 * Logs a ShareDB read denial. Only `client_server_mismatch` goes to Rollbar;
 * revoked / not-viewable cases are expected during unrelease races.
 */
export const logReadPermissionDenied = (
  context: string,
  docId: string,
  err: { message?: string },
  hadPermissionAtRequest: boolean,
  action: DocReadAction,
) => {
  const websocket = store.getState().auth.websocket;
  const reason = getDocErrorReason(docId, hadPermissionAtRequest, action);
  const payload = {
    docId,
    user_id: websocket?.user_id,
    err: err.message,
    reason,
  };
  if (reason === "client_server_mismatch") {
    rollbarAndLogWarning(context, payload);
  } else {
    logInfoWithDebugFlag("debug_sharedb", context, payload);
  }
};

/** True for ShareDB read denials, including hard-rollback wrappers that embed the code. */
export const isMissingReadPermissionError = (err: unknown): boolean => {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "object" && err !== null && "message" in err
        ? String(err.message)
        : typeof err === "string"
          ? err
          : "";
  return message.includes(AUTH_ERRORS.MISSING_READ_PERMISSION);
};

/**
 * Connection-level safety net for uncaught ShareDB errors. Classifies
 * MISSING_READ_PERMISSION when a doc id is present; otherwise warns — we cannot
 * prove the denial is expected without client permission context.
 */
export const handleShareDBConnectionReadPermissionError = (
  err: unknown,
  context: { sectionId: string | null; userId: string },
): boolean => {
  if (!isMissingReadPermissionError(err)) return false;

  const message =
    typeof err === "object" && err !== null && "message" in err ? String(err.message) : undefined;

  const docId = getDocIdFromShareDBError(err);
  if (docId) {
    logReadPermissionDenied(
      "[ShareDBConnection] Read permission denied",
      docId,
      { message },
      // No at-request snapshot on the connection path — current canViewPage alone
      // separates mismatch (unexpected) from not-viewable (expected race).
      false,
      "fetch",
    );
    return true;
  }

  rollbarAndLogWarning("[ShareDBConnection] Read permission denied (no doc context)", {
    err: message,
    sectionId: context.sectionId,
    userId: context.userId,
  });
  return true;
};
